import { Box, Button, FileInput, Group, LoadingOverlay, Modal, Select, Space, Stack, Text } from '@mantine/core';
import { IconFile } from '@tabler/icons-react';
import TemplateSelectModal from '../../List/ui/TemplateSelectModal';
import { useState } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import usePostPresignedUrl from '../../List/hooks/usePostPresignedUrl';
import usePostUploadCompleted from '../../List/hooks/usePostUploadCompleted';
import { useCheckDuplicateFileName } from '../../../Manual/List/hooks/useCheckDupliactedFileName';
import { buildSearchString } from '../../../utils/buildSearchString';
import { useModelOptions } from '../../../Common/hooks/useModelOptions';

interface IPropsUploadModal {
  opened: boolean;
  onClose: () => void;
  companyName: string;
}

export default function AsCsFileUploadModal(props: IPropsUploadModal) {
  const { opened, onClose, companyName } = props;
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
  const [selectedTemplateName, setSelectedTemplateName] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [templateModalOpened, { open: openTemplateModal, close: closeTemplateModal }] = useDisclosure(false);
  const [selectedModelId, setSelectedModelId] = useState<number | null>(null);
  const isCsvFile = file?.name.toLowerCase().endsWith('.csv') || false;

  const params = useParams();
  const companyId = Number(params.companyId);
  const modelOptions = useModelOptions(companyId, { includeAll: false });

  const resetStates = () => {
    setFile(null);
    setSelectedTemplateId(null);
    setSelectedTemplateName(null);
    setIsLoading(false);
  };

  const { mutateAsync: postPresignedUrl } = usePostPresignedUrl();
  const { mutate: postUploadCompleted } = usePostUploadCompleted();

  const sendData = {
    companyId,
    fileName: file?.name || '',
  };

  const { refetch: refetchCheckDuplicateFile } = useCheckDuplicateFileName(sendData);

  const checkDuplicateFile = async (data: { fileName: string }): Promise<'duplicate-confirmed' | 'duplicate-canceled' | 'ok'> => {
    if (!data.fileName) return 'ok';

    const { data: result } = await refetchCheckDuplicateFile();

    if (result === 'Y') {
      const confirmResult = await Swal.fire({
        icon: 'warning',
        html: `<p>동일한 파일명이 존재합니다. <br/> 업로드 파일을 다시 확인해주세요.</p>`,
        confirmButtonText: '무시하고 업로드',
        showCancelButton: true,
        cancelButtonText: '닫기',
      });

      if (confirmResult.isConfirmed) {
        return 'duplicate-confirmed';
      } else {
        return 'duplicate-canceled';
      }
    }

    return 'ok';
  };

  const fetchPresignedUrl = async (data: {
    contentType: string;
    presignedUrl: string;
    fileUploadId: number;
    fileKey: string;
    companyId: number;
    modelId: number;
    uploadDate: string;
    version: string;
  }) => {
    if (!file || !selectedModelId) return;
    setIsLoading(true);
    const result = await fetch(data.presignedUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': data.contentType },
    });
    if (result.ok) {
      setIsLoading(false);
      postUploadCompleted({
        fileUploadId: data.fileUploadId,
        fileSize: file.size,
      });

      resetStates();
      onClose();
      navigate(
        `/admin/management/aiagent/company/${companyId}/ascs/file${buildSearchString({
          companyName,
        })}`,
      );
    } else {
      throw new Error('file upload failed');
    }
  };

  const onClickUpload = async () => {
    if (!selectedModelId) {
      Swal.fire({
        icon: 'warning',
        title: '기계 모델을 선택해주세요.',
        confirmButtonText: '확인',
      });
      return;
    }

    if (!file) {
      Swal.fire({
        icon: 'warning',
        title: '파일을 선택해주세요.',
        confirmButtonText: '확인',
      });
      return;
    }

    // AS_CS이고 CSV일 때 템플릿 선택 필수
    if (isCsvFile && !selectedTemplateId) {
      openTemplateModal();
      return;
    }

    const duplicateStatus = await checkDuplicateFile(sendData);
    if (duplicateStatus === 'duplicate-canceled') {
      return;
    }

    try {
      const result = await postPresignedUrl({
        modelId: selectedModelId || 0,
        fileName: file?.name || '',
        templateId: selectedTemplateId || undefined,
      });
      if (!result.data.presignedUrl) {
        throw new Error('Presigned URL not available');
      }

      await fetchPresignedUrl(result.data);
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: '파일 업로드 실패',
        text: '파일 업로드 중 오류가 발생했습니다. 다시 시도해주세요.',
      });
      return;
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={() => {
        resetStates();
        onClose();
      }}
      centered
      title="파일 업로드"
    >
      <Select
        mb="md"
        searchable
        placeholder="기계 모델을 선택해주세요"
        value={selectedModelId !== null ? String(selectedModelId) : ''}
        onChange={(value) => setSelectedModelId(value ? Number(value) : null)}
        data={modelOptions}
      />
      <FileInput
        value={file}
        onChange={setFile}
        accept={'.csv, .pdf'}
        leftSection={<IconFile size={16} />}
        placeholder="파일을 선택해주세요"
      />
      {isCsvFile && companyId && (
        <>
          <Space h="md" />
          <Box>
            <Text size="sm" fw={500} mb="xs">
              템플릿 선택
            </Text>
            <Button variant={selectedTemplateId ? 'light' : 'outline'} fullWidth onClick={openTemplateModal}>
              {selectedTemplateName || '템플릿을 선택해주세요'}
            </Button>
          </Box>
        </>
      )}
      <Space h="md" />
      <Stack gap="sm" p="xs">
        <Text fw="bold" size="sm">
          ※유의사항
        </Text>
        <Text size="sm">&#8226; 기계 모델별로 파일을 업로드해주세요. 해당 파일은 선택된 기계 모델 데이터에 업데이트 됩니다.</Text>
        <Text size="sm">&#8226; AI가 인식하기 어려운 내용은 학습되지 않을 수 있습니다.</Text>
      </Stack>
      <Group justify="center" mt="lg">
        <Button
          variant="outline"
          onClick={() => {
            resetStates();
            onClose();
          }}
        >
          취소
        </Button>
        <Button type="button" onClick={onClickUpload}>
          저장
        </Button>
      </Group>
      <LoadingOverlay
        visible={isLoading}
        zIndex={1000}
        overlayProps={{ radius: 'sm', blur: 1 }}
        loaderProps={{ color: 'blue' }}
      />
      <TemplateSelectModal
        opened={templateModalOpened}
        close={closeTemplateModal}
        companyId={companyId}
        selectedTemplateId={selectedTemplateId}
        onSelect={(templateId: number, templateName: string) => {
          setSelectedTemplateId(templateId);
          setSelectedTemplateName(templateName);
          closeTemplateModal();
        }}
      />
    </Modal>
  );
}
